// TSE Workspace — Employee Wall (PDF page 11)
import React, { useRef, useState } from 'react';
import { wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Tabs, Avatar, Select, Input, Field, Modal, Kebab, Empty, Loading, Spin, FileChip,
  useAsync, useAction, uploadFiles, promptText, confirmAction, timeAgo, fmtDate, pad2, BUSINESS_UNITS, DEPARTMENTS,
} from '../../components/ui';
import { notify } from '../../utils/notify';
import { openAttachment, expenseAPI } from '../../services/api';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CAT_LABEL = { update: 'Update', announcement: 'Company announcement', recognition: 'Team recognition', team: 'Team update' };
const AUDIENCES = ['All employees', ...BUSINESS_UNITS, ...DEPARTMENTS.filter((d) => !BUSINESS_UNITS.includes(d))];
const QL_ICON = { K: <span style={{ background: '#1f5fbf', color: '#fff', fontWeight: 800, width: 22, height: 22, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.736rem' }}>K</span>, mail: <i className="bi bi-envelope-open" style={{ color: '#1479bd' }} />, chat: <i className="bi bi-chat-dots" style={{ color: '#1479bd' }} />, check: <i className="bi bi-check2-all" style={{ color: '#d63a2f' }} />, clock: <i className="bi bi-clock" style={{ color: '#1479bd' }} /> };

function ImageGrid({ attachments }) {
  const imgs = (attachments || []).filter((a) => /\.(png|jpe?g|webp|gif)$/i.test(a.fileName || ''));
  const files = (attachments || []).filter((a) => !imgs.includes(a));
  const [urls, setUrls] = useState({});
  React.useEffect(() => {
    imgs.forEach((a) => {
      if (!urls[a.s3Key]) expenseAPI.getAttachmentUrl(a.s3Key).then((d) => setUrls((u) => ({ ...u, [a.s3Key]: d.url }))).catch(() => {});
    });
  }, [attachments]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!imgs.length && !files.length) return null;
  return (
    <>
      {imgs.length > 0 && <div className="tse-img-grid">{imgs.map((a) => (urls[a.s3Key] ? <img key={a.s3Key} src={urls[a.s3Key]} alt={a.fileName} onClick={() => window.open(urls[a.s3Key], '_blank')} /> : <div key={a.s3Key} className="tse-skel" style={{ height: 140 }} />))}</div>}
      {files.length > 0 && <div className="tse-stack" style={{ gap: 6, padding: '0 13px 10px' }}>{files.map((a) => <FileChip key={a.s3Key} file={a} onOpen={() => openAttachment(a).catch(() => notify.error('Could not open'))} />)}</div>}
    </>
  );
}

function Post({ post, onChange, canModerate }) {
  const [showComments, setShowComments] = useState(false);
  const [text, setText] = useState('');
  const [busy, run] = useAction();
  const upd = (p) => p && onChange(p.post || p);
  const like = async () => upd(await run('like', () => wsAPI.likePost(post._id), false));
  const comment = async () => {
    if (!text.trim()) return;
    const r = await run('comment', () => wsAPI.commentPost(post._id, text.trim()), false);
    if (r) { setText(''); upd(r); }
  };
  const report = async () => {
    const reason = await promptText({ title: 'Report this post', label: 'Reason', placeholder: 'Why should moderators review this post?', confirmText: 'Report' });
    if (reason === null) return;
    upd(await run('report', () => wsAPI.reportPost(post._id, reason)));
  };
  const remove = async () => {
    if (!(await confirmAction('Remove this post?', 'It will be hidden from the wall.', 'Remove', '#c43c31'))) return;
    const r = await run('del', () => wsAPI.deletePost(post._id), 'Post removed');
    if (r) onChange(null);
  };
  const tone = post.category === 'announcement' ? '' : post.category === 'recognition' ? 'green' : '';
  const lines = String(post.content || '').split('\n');
  const tagsLine = lines.filter((l) => /^\s*(#\w+\s*)+$/.test(l)).join(' ');
  const body = lines.filter((l) => !/^\s*(#\w+\s*)+$/.test(l)).join('\n');

  return (
    <div className="tse-post">
      <div className="tse-post-head">
        <Avatar name={post.authorLabel || post.authorName} tone={tone || (post.category === 'announcement' ? '' : 'green')} />
        <div style={{ minWidth: 0 }}>
          <div className="n">{post.authorLabel || post.authorName}{post.pinned && <i className="bi bi-pin-angle-fill" style={{ color: '#1479bd', marginLeft: 8 }} title="Pinned" />}</div>
          <div className="m">{CAT_LABEL[post.category] || 'Update'} &nbsp;•&nbsp; {timeAgo(post.createdAt)}{post.audience && post.audience !== 'All employees' ? ` · ${post.audience}` : ''}{post.authorLabel && post.authorLabel !== post.authorName ? ` · by ${post.authorName}` : ''}</div>
        </div>
        <div className="tse-spacer" />
        <button type="button" className="tse-btn ghost sm" style={{ color: post.reportedByMe ? '#c0392b' : '#33475b' }} onClick={report} disabled={post.reportedByMe}><i className={`bi ${post.reportedByMe ? 'bi-flag-fill' : 'bi-flag'}`} />{post.reportedByMe ? 'Reported' : 'Report post'}</button>
        <Kebab items={[
          { label: post.pinned ? 'Unpin' : 'Pin to top', icon: 'bi-pin-angle', hidden: !canModerate, onClick: async () => upd(await run('pin', () => wsAPI.pinPost(post._id), false)) },
          { label: 'Remove post', icon: 'bi-trash', danger: true, hidden: !post.canDelete && !canModerate, onClick: remove },
          { label: 'Copy text', icon: 'bi-clipboard', onClick: () => navigator.clipboard?.writeText(post.content).then(() => notify.info('Copied')) },
        ]} />
      </div>

      {post.event && post.event.date ? (
        <div className="tse-post-event">
          <i className="bi bi-megaphone-fill ic" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '1.209rem', fontWeight: 700 }}>{post.title}</div>
            <div className="ev-meta">
              <span><i className="bi bi-calendar3" /> {new Date(post.event.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</span>
              {post.event.time && <span><i className="bi bi-clock" /> {post.event.time}</span>}
              {post.event.location && <span><i className="bi bi-geo-alt-fill" /> {post.event.location}</span>}
            </div>
            <div style={{ color: '#33475b', whiteSpace: 'pre-wrap' }}>{body}</div>
            {post.event.link && <a href={post.event.link} target="_blank" rel="noreferrer" className="tse-btn sm" style={{ marginTop: 8 }}>Join the conversation <i className="bi bi-arrow-right" /></a>}
          </div>
          <div className="d-none d-lg-block" style={{ borderLeft: '1px solid #c9d8e6', paddingLeft: 14, color: '#1a8ad4', fontWeight: 600, fontSize: '0.989rem', lineHeight: 1.25, alignSelf: 'center', minWidth: 170 }}>People<br />Projects<br />A More<br />Sustainable<br />Tomorrow<div style={{ width: 32, height: 3, background: '#1a8ad4', marginTop: 8 }} /></div>
        </div>
      ) : (
        <div className="tse-post-body">
          {post.title && <h3>{post.title}</h3>}
          {body}
        </div>
      )}
      {(tagsLine || (post.hashtags || []).length > 0) && <div className="tse-hashtags" style={{ padding: '0 13px 10px' }}>{(post.hashtags || []).map((h) => <span key={h}>{h}</span>)}</div>}
      <ImageGrid attachments={post.attachments} />
      <div className="tse-post-actions">
        <button type="button" className={post.likedByMe ? 'on' : ''} onClick={like} disabled={busy === 'like'}><i className={`bi ${post.likedByMe ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'}`} />{post.likeCount || ''} Like</button>
        <button type="button" onClick={() => setShowComments((s) => !s)}><i className="bi bi-chat" />{(post.comments || []).length || ''} Comment</button>
        {canModerate && post.reportCount > 0 && <span className="tse-small" style={{ marginLeft: 'auto', color: '#c0392b', alignSelf: 'center' }}><i className="bi bi-flag-fill" /> {post.reportCount} report(s)</span>}
      </div>
      {showComments && (
        <div style={{ paddingBottom: 7 }}>
          {(post.comments || []).map((c) => (
            <div key={c._id} className="tse-comment">
              <Avatar name={c.byName} size="sm" />
              <div className="bubble"><strong>{c.byName}</strong> <span className="tse-muted tse-small">{timeAgo(c.at)}</span><div>{c.text}</div></div>
            </div>
          ))}
          <div className="tse-row" style={{ padding: '4px 13px', flexWrap: 'nowrap' }}>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment…" onKeyDown={(e) => { if (e.key === 'Enter') comment(); }} />
            <button type="button" className="tse-btn sm" onClick={comment} disabled={busy === 'comment'}>Post</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EmployeeWall() {
  const { user } = useAuth();
  const canModerate = ['admin', 'hr'].includes(user?.role);
  const canEvent = ['admin', 'hr', 'hod', 'manager'].includes(user?.role);
  const [tab, setTab] = useState('all');
  const catParam = { all: undefined, announcements: 'announcement', recognition: 'recognition', teams: 'team' }[tab];
  const posts = useAsync(() => wsAPI.posts({ category: catParam, limit: 30 }), [tab]);
  const events = useAsync(() => wsAPI.events({ limit: 10 }), [], { silent: true });
  const celebrations = useAsync(() => wsAPI.celebrations(), [], { silent: true });
  const links = useAsync(() => wsAPI.quickLinks(), [], { silent: true });

  const [text, setText] = useState('');
  const [audience, setAudience] = useState(user?.businessUnit || 'All employees');
  const [category, setCategory] = useState('update');
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [bigPost, setBigPost] = useState(null);
  const [evModal, setEvModal] = useState(null);
  const [allEvents, setAllEvents] = useState(false);
  const fileRef = useRef();
  const composerRef = useRef();

  const setPost = (id) => (p) => posts.setData((d) => ({ ...d, posts: p ? d.posts.map((x) => (x._id === id ? p : x)) : d.posts.filter((x) => x._id !== id) }));

  const publish = async (payload) => {
    setPosting(true);
    try {
      const attachments = payload.files?.length ? await uploadFiles(payload.files, 'wall') : [];
      const r = await wsAPI.createPost({ ...payload, files: undefined, attachments });
      notify.success('Posted to the wall');
      posts.setData((d) => ({ ...d, posts: [r.post, ...(d?.posts || [])] }));
      return true;
    } catch (e) {
      notify.error(e.message || 'Could not post');
      return false;
    } finally {
      setPosting(false);
    }
  };
  const quickPost = async () => {
    if (!text.trim()) { notify.info('Write something to share'); return; }
    if (await publish({ content: text.trim(), audience, category, files })) { setText(''); setFiles([]); }
  };

  const evs = events.data?.events || [];
  const anniv = celebrations.data?.anniversaries || [];
  const bdays = celebrations.data?.birthdays || [];

  return (
    <PageShell section="TSE Workspace">
      <div className="tse-split" style={{ gridTemplateColumns: 'minmax(0, 1fr) 380px' }}>
        <div className="tse-stack" style={{ gap: 8 }}>
          <PageHead title="Employee Wall" subtitle="Connect  •  Share  •  Celebrate"
            actions={<button type="button" className="tse-btn lg" onClick={() => setBigPost({ category: canModerate ? 'announcement' : 'recognition', audience: 'All employees', title: '', content: '', event: { date: '', time: '', location: '', link: '' }, files: [] })}><i className="bi bi-plus-circle" />Create post</button>} />
          <div style={{ marginTop: -14 }}>
            <Tabs variant="card-tabs" value={tab} onChange={setTab} tabs={[{ key: 'all', label: 'All updates' }, { key: 'announcements', label: 'Announcements' }, { key: 'recognition', label: 'Recognition' }, { key: 'teams', label: 'Teams' }]} />
          </div>

          <div className="tse-card" ref={composerRef}>
            <div className="tse-card-body" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <Avatar name={user?.userName} size="lg" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <textarea className="tse-input" rows={text ? 3 : 1} style={{ minHeight: 36 }} value={text} onChange={(e) => setText(e.target.value)} placeholder="Share an update with your team..." />
                {files.length > 0 && <div className="tse-grid g-2" style={{ marginTop: 8 }}>{files.map((f, i) => <FileChip key={i} file={f} onRemove={() => setFiles((p) => p.filter((_, j) => j !== i))} />)}</div>}
                <div className="tse-row" style={{ marginTop: 8 }}>
                  <i className="bi bi-people" style={{ fontSize: '1.032rem' }} /><span className="tse-small">Audience:</span>
                  <Select value={audience} onChange={(e) => setAudience(e.target.value)} options={AUDIENCES} style={{ width: 230 }} />
                  <Select value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 170 }} options={[{ value: 'update', label: 'Update' }, { value: 'recognition', label: 'Recognition' }, { value: 'team', label: 'Team update' }, ...(canModerate ? [{ value: 'announcement', label: 'Announcement' }] : [])]} />
                  <div className="tse-spacer" />
                  <input ref={fileRef} type="file" hidden multiple accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || []).filter((f) => f.size <= 10485760)]); e.target.value = ''; }} />
                  <button type="button" className="tse-btn ghost" style={{ color: '#33475b' }} onClick={() => fileRef.current?.click()}><i className="bi bi-paperclip" />Attach</button>
                  <button type="button" className="tse-btn" style={{ minWidth: 110 }} onClick={quickPost} disabled={posting}>{posting ? <Spin /> : null}Post</button>
                </div>
              </div>
            </div>
          </div>

          {posts.loading ? <Loading /> : !(posts.data?.posts || []).length ? <Card><Empty icon="bi-chat-square-text" title="No posts yet" sub="Be the first to share an update." /></Card>
            : posts.data.posts.map((p) => <Post key={p._id} post={p} onChange={setPost(p._id)} canModerate={canModerate} />)}
        </div>

        <div className="tse-stack" style={{ gap: 8 }}>
          <Card title={<span><i className="bi bi-calendar-event" style={{ color: '#1479bd', marginRight: 10 }} />Upcoming events</span>} actions={<>{canEvent && <button type="button" className="link" onClick={() => setEvModal({ title: '', description: '', date: '', startTime: '', endTime: '', mode: 'Teams' })}>+ Add</button>}<button type="button" className="link" onClick={() => setAllEvents(true)}>View all</button></>} bodyClass="tight">
            {events.loading ? <Loading /> : !evs.length ? <div className="tse-muted tse-small" style={{ padding: 6 }}>No upcoming events.</div> : evs.slice(0, 3).map((e) => (
              <div key={e._id} className="tse-row" style={{ flexWrap: 'nowrap', gap: 8, padding: '7px 3px', borderBottom: '1px solid #e1e9f1' }}>
                <div className="tse-datebox"><div className="d">{pad2(new Date(e.date).getDate())}</div><div className="m">{MON[new Date(e.date).getMonth()]}</div></div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="tse-strong">{e.title}</div>
                  {e.description && <div className="tse-small tse-muted tse-ellipsis">{e.description}</div>}
                  <div className="tse-small tse-muted">{[e.startTime && `${e.startTime}${e.endTime ? ` – ${e.endTime}` : ''}`, e.mode].filter(Boolean).join('  |  ')}</div>
                </div>
                <i className="bi bi-chevron-right" />
              </div>
            ))}
          </Card>

          <Card title={<span><i className="bi bi-gift" style={{ color: '#1479bd', marginRight: 10 }} />Celebrations</span>} bodyClass="tight">
            <div className="tse-strong" style={{ padding: '0 3px 4px' }}>Work anniversaries</div>
            {!anniv.length && <div className="tse-muted tse-small" style={{ padding: '0 3px 6px' }}>No anniversaries in the next 30 days.</div>}
            {anniv.slice(0, 4).map((a) => (
              <div key={a.id} className="tse-row" style={{ flexWrap: 'nowrap', padding: '6px 3px', borderBottom: '1px solid #e1e9f1' }}>
                <Avatar name={a.userName} />
                <div><div className="tse-strong">{a.userName}</div><div className="tse-small tse-muted">Completes {a.years} year{a.years > 1 ? 's' : ''}  •  {pad2(new Date(a.date).getDate())} {MON[new Date(a.date).getMonth()]}</div></div>
              </div>
            ))}
            {bdays.length > 0 && <div className="tse-strong" style={{ padding: '7px 3px 4px' }}>Birthdays</div>}
            {bdays.slice(0, 3).map((b) => (
              <div key={b.id} className="tse-row" style={{ flexWrap: 'nowrap', padding: '4px 3px' }}><Avatar name={b.userName} size="sm" /><div className="tse-small"><strong>{b.userName}</strong> · {fmtDate(b.date)}</div></div>
            ))}
          </Card>

          <Card title={<span><i className="bi bi-link-45deg" style={{ color: '#1479bd', marginRight: 8 }} />Quick links</span>} bodyClass="tight">
            {(links.data?.links || []).map((l) => (
              <a key={l.label} href={l.url} target={l.url.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="tse-row" style={{ flexWrap: 'nowrap', padding: '7px 4px', borderBottom: '1px solid #e1e9f1', textDecoration: 'none', color: '#0f2537' }}>
                <span style={{ width: 26, textAlign: 'center', fontSize: '0.946rem' }}>{QL_ICON[l.icon] || <i className="bi bi-box-arrow-up-right" />}</span>
                <span style={{ flex: 1 }}>{l.label}</span>
                <i className="bi bi-box-arrow-up-right" />
              </a>
            ))}
          </Card>
        </div>
      </div>

      <Modal open={!!bigPost} onClose={() => setBigPost(null)} size="lg" title="Create post"
        footer={<><button type="button" className="tse-btn gray" onClick={() => setBigPost(null)}>Cancel</button><button type="button" className="tse-btn" disabled={posting} onClick={async () => {
          if (!bigPost.content.trim()) { notify.error('Write the post'); return; }
          const ev = bigPost.category === 'announcement' && bigPost.event.date ? bigPost.event : undefined;
          if (await publish({ ...bigPost, event: ev })) setBigPost(null);
        }}>{posting ? <Spin /> : null}Publish</button></>}>
        {bigPost && (
          <div className="tse-stack" style={{ gap: 8 }}>
            <div className="tse-form-grid c2">
              <Field label="Post type"><Select value={bigPost.category} onChange={(e) => setBigPost({ ...bigPost, category: e.target.value })} options={[{ value: 'update', label: 'Update' }, { value: 'recognition', label: 'Recognition' }, { value: 'team', label: 'Team update' }, ...(canModerate ? [{ value: 'announcement', label: 'Company announcement' }] : [])]} /></Field>
              <Field label="Audience"><Select value={bigPost.audience} onChange={(e) => setBigPost({ ...bigPost, audience: e.target.value })} options={AUDIENCES} /></Field>
              <Field label="Posting as" className="span-all" hint="e.g. TSE Communications, Water Business Unit (leave blank to post as yourself)"><Input value={bigPost.authorLabel || ''} onChange={(e) => setBigPost({ ...bigPost, authorLabel: e.target.value })} /></Field>
              <Field label="Headline" className="span-all"><Input value={bigPost.title} onChange={(e) => setBigPost({ ...bigPost, title: e.target.value })} placeholder="e.g. Celebrating safe project execution!" /></Field>
            </div>
            <Field label="Message" required><textarea className="tse-input" rows={5} value={bigPost.content} onChange={(e) => setBigPost({ ...bigPost, content: e.target.value })} placeholder="Share the details… add #hashtags" /></Field>
            {bigPost.category === 'announcement' && (
              <div className="tse-form-grid c2" style={{ background: '#f4f8fc', padding: 10, borderRadius: 8 }}>
                <Field label="Event date"><Input type="date" value={bigPost.event.date} onChange={(e) => setBigPost({ ...bigPost, event: { ...bigPost.event, date: e.target.value } })} /></Field>
                <Field label="Time"><Input value={bigPost.event.time} onChange={(e) => setBigPost({ ...bigPost, event: { ...bigPost.event, time: e.target.value } })} placeholder="4:00 PM – 5:00 PM IST" /></Field>
                <Field label="Location"><Input value={bigPost.event.location} onChange={(e) => setBigPost({ ...bigPost, event: { ...bigPost.event, location: e.target.value } })} placeholder="Microsoft Teams (Online)" /></Field>
                <Field label="Join link"><Input value={bigPost.event.link} onChange={(e) => setBigPost({ ...bigPost, event: { ...bigPost.event, link: e.target.value } })} placeholder="https://" /></Field>
              </div>
            )}
            <Field label="Images / files">
              <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="tse-input" style={{ paddingTop: 6 }} onChange={(e) => setBigPost({ ...bigPost, files: Array.from(e.target.files || []) })} />
            </Field>
          </div>
        )}
      </Modal>

      <Modal open={!!evModal} onClose={() => setEvModal(null)} title="Add event"
        footer={<><button type="button" className="tse-btn gray" onClick={() => setEvModal(null)}>Cancel</button><button type="button" className="tse-btn" onClick={async () => {
          if (!evModal.title || !evModal.date) { notify.error('Title and date are required'); return; }
          try { await wsAPI.createEvent(evModal); notify.success('Event added'); setEvModal(null); events.reload(); } catch (e) { notify.error(e.message); }
        }}>Save event</button></>}>
        {evModal && (
          <div className="tse-form-grid c2">
            <Field label="Title" required className="span-all"><Input value={evModal.title} onChange={(e) => setEvModal({ ...evModal, title: e.target.value })} /></Field>
            <Field label="Description" className="span-all"><Input value={evModal.description} onChange={(e) => setEvModal({ ...evModal, description: e.target.value })} /></Field>
            <Field label="Date" required><Input type="date" value={evModal.date} onChange={(e) => setEvModal({ ...evModal, date: e.target.value })} /></Field>
            <Field label="Mode"><Select value={evModal.mode} onChange={(e) => setEvModal({ ...evModal, mode: e.target.value })} options={['Teams', 'Office', 'Site', 'Zoom']} /></Field>
            <Field label="Start time"><Input value={evModal.startTime} onChange={(e) => setEvModal({ ...evModal, startTime: e.target.value })} placeholder="10:00 AM" /></Field>
            <Field label="End time"><Input value={evModal.endTime} onChange={(e) => setEvModal({ ...evModal, endTime: e.target.value })} placeholder="11:00 AM IST" /></Field>
          </div>
        )}
      </Modal>

      <Modal open={allEvents} onClose={() => setAllEvents(false)} title="Upcoming events">
        {!evs.length ? <Empty title="No upcoming events" /> : evs.map((e) => (
          <div key={e._id} className="tse-row" style={{ flexWrap: 'nowrap', padding: '7px 0', borderBottom: '1px solid #e1e9f1' }}>
            <div className="tse-datebox"><div className="d">{pad2(new Date(e.date).getDate())}</div><div className="m">{MON[new Date(e.date).getMonth()]}</div></div>
            <div style={{ flex: 1 }}><div className="tse-strong">{e.title}</div><div className="tse-small tse-muted">{e.description}</div><div className="tse-small tse-muted">{e.startTime} {e.endTime && `– ${e.endTime}`} {e.mode && `| ${e.mode}`}</div></div>
            {canModerate && <button type="button" className="tse-btn xs danger-outline" onClick={async () => { await wsAPI.deleteEvent(e._id).catch(() => {}); events.reload(); }}><i className="bi bi-trash" /></button>}
          </div>
        ))}
      </Modal>
    </PageShell>
  );
}
